import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api';
import { Plus, Sparkles, X, Trash2, Check, Download, Edit2, Copy, Layers, Link2, Wand2, Search, Filter } from 'lucide-react';
import RowActions from '../components/RowActions';
import { downloadBlob, extractAxiosErrorMessage } from '../utils/download';
import { useConfirm } from '../components/ConfirmDialog';
import ImportAIModal from '../components/ImportAIModal';
import PreventivoLinkModal from '../components/preventivi/PreventivoLinkModal';
import PreventivoAIGenModal from '../components/preventivi/PreventivoAIGenModal';
import PreventivoDetailModal from '../components/preventivi/PreventivoDetailModal';
import PreventivoFormModal from '../components/preventivi/PreventivoFormModal';
import ImportSezioneModal from '../components/preventivi/ImportSezioneModal';

export default function PreventiviPage({ context }) {
  const confirm = useConfirm();
  const [preventivi, setPreventivi] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [editing, setEditing] = useState(null);
  const [aiDesc, setAiDesc] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [form, setForm] = useState({ titolo: '', oggetto: '', iva_default: 22, applica_iva: true, sconto_globale_pct: 0, pratica_id: context?.pratica_id || '', note_finali: '', esclusioni: '', righe: [], sezioni: [] });
  // selectedSection: null = righe flat, oppure indice della sezione attiva per "Aggiungi voce"
  const [selectedSection, setSelectedSection] = useState(null);
  const [prezzario, setPrezzario] = useState([]);
  const [prezzSearch, setPrezzSearch] = useState('');
  const [cataloghi, setCataloghi] = useState([]);
  const [selectedCatalogo, setSelectedCatalogo] = useState('');
  // Link a pratica/cantiere/computo
  const [linkModal, setLinkModal] = useState(null);  // { tipo, preventivo, items, search }
  const [pratiche, setPratiche] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [computi, setComputi] = useState([]);
  const [showImportAI, setShowImportAI] = useState(false);
  const [showImportSezione, setShowImportSezione] = useState(false);
  // Clienti pagatori della pratica corrente del form (per attribuzione righe)
  const [clientiPagatori, setClientiPagatori] = useState([]);
  // Ricerca / filtri elenco
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState('');
  const [clientiAll, setClientiAll] = useState([]);
  const [clienteFilter, setClienteFilter] = useState('');

  // Quando cambia la pratica del form, ricarico i clienti pagatori
  useEffect(() => {
    const pid = form.pratica_id;
    if (!pid) { setClientiPagatori([]); return; }
    API.get(`/api/pratiche/${pid}/clienti`)
      .then(r => setClientiPagatori(r.data?.clienti || []))
      .catch(() => setClientiPagatori([]));
  }, [form.pratica_id]);

  useEffect(() => {
    API.get('/api/pratiche').then(r => setPratiche(r.data || [])).catch(() => {});
    API.get('/api/cantieri').then(r => setCantieri(r.data || [])).catch(() => {});
    API.get('/api/computi').then(r => setComputi(r.data || [])).catch(() => {});
    API.get('/api/clienti').then(r => setClientiAll(r.data || [])).catch(() => {});
  }, []);

  const LINK_CONFIG = {
    pratica:  { field: 'pratica_id',  label: 'Pratica',         endpoint: 'link-pratica',  collection: 'pratiche', items: () => pratiche,  color: 'emerald', placeholder: '🔍 Cerca pratica (titolo, n°, cliente)…' },
    cantiere: { field: 'cantiere_id', label: 'Cantiere',        endpoint: 'link-cantiere', collection: 'cantieri', items: () => cantieri,  color: 'amber',   placeholder: '🔍 Cerca cantiere (titolo, codice, indirizzo)…' },
    computo:  { field: 'computo_id',  label: 'Computo Metrico', endpoint: 'link-computo',  collection: 'computi',  items: () => computi,   color: 'sky',     placeholder: '🔍 Cerca computo (titolo, codice)…' },
  };

  const openLink = (tipo, prev) => setLinkModal({ tipo, preventivo: prev, search: '' });

  const doLink = async (targetId) => {
    const cfg = LINK_CONFIG[linkModal.tipo];
    try {
      const body = { [cfg.field]: targetId };
      await API.post(`/api/preventivi/${linkModal.preventivo.id}/${cfg.endpoint}`, body);
      setLinkModal(null);
      load();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const doUnlink = async () => {
    const cfg = LINK_CONFIG[linkModal.tipo];
    if (!window.confirm(`Vuoi scollegare il preventivo dal/dalla ${cfg.label.toLowerCase()}?`)) return;
    try {
      await API.post(`/api/preventivi/${linkModal.preventivo.id}/${cfg.endpoint}`, { [cfg.field]: null });
      setLinkModal(null);
      load();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const getInfo = (tipo, id) => (LINK_CONFIG[tipo].items() || []).find(x => x.id === id);
  const getName = (item, tipo) => {
    if (!item) return null;
    if (tipo === 'pratica') return item.titolo || item.numero_pratica;
    if (tipo === 'cantiere') return item.titolo || item.codice_cantiere;
    if (tipo === 'computo') return item.titolo || item.codice_computo;
    return item.titolo;
  };

  const load = useCallback(() => {
    const params = context?.pratica_id ? { pratica_id: context.pratica_id } : {};
    API.get('/api/preventivi', { params }).then(r => setPreventivi(r.data));
  }, [context?.pratica_id]);
  useEffect(() => { load(); }, [load]);

  // Import AI: crea un nuovo preventivo con tutte le voci estratte (modalità flat).
  const handleImportAI = async (voci, meta, sampleId) => {
    const titoloDefault = meta?.filename
      ? `Importato: ${meta.filename.replace(/\.[^.]+$/, '')}`
      : 'Preventivo importato con AI';
    const righe = voci.map((v, i) => {
      const isCorpo = v.tipo === 'corpo';
      const qta = isCorpo ? 1 : Number(v.quantita || 0);
      const pu = isCorpo
        ? Number(v.importo || v.prezzo_unitario || 0)
        : Number(v.prezzo_unitario || 0);
      return {
        ordine: i + 1,
        descrizione: v.descrizione,
        unita_misura: isCorpo ? 'a corpo' : (v.unita_misura || ''),
        quantita: qta,
        prezzo_unitario: pu,
        sconto: 0,
        flag_a_corpo: isCorpo,
      };
    });
    await API.post('/api/preventivi', {
      titolo: titoloDefault,
      oggetto: '',
      iva_default: 22,
      pratica_id: context?.pratica_id || '',
      note_finali: '',
      esclusioni: '',
      righe,
      sezioni: [],
    });
    if (sampleId) {
      API.post(`/api/import/${sampleId}/confirm`, { voci }).catch(() => {});
    }
    setShowImportAI(false);
    await load();
  };

  // Apertura automatica detail se navighiamo dalla PraticaHub con preventivo_id
  useEffect(() => {
    if (!context?.preventivo_id || !preventivi || preventivi.length === 0) return;
    const target = preventivi.find(p => p.id === context.preventivo_id);
    if (target) setShowDetail(target);
  }, [context?.preventivo_id, preventivi]);


  useEffect(() => {
    API.get('/api/prezzario/cataloghi').then(r => {
      setCataloghi(r.data);
      // Default: preferisci CER 2025 (catalogo aggiornato), altrimenti il primo disponibile
      if (r.data.length && !selectedCatalogo) {
        const cer = r.data.find(c => c.id === 'cer_2025');
        setSelectedCatalogo(cer ? 'cer_2025' : r.data[0].id);
      }
    });
  }, []);

  useEffect(() => {
    const params = { limit: 50 };
    if (prezzSearch) params.q = prezzSearch;
    if (selectedCatalogo) params.catalogo = selectedCatalogo;
    // Semantic search per query >= 3 char (sui cataloghi reali)
    if (prezzSearch && prezzSearch.length >= 3) {
      const semParams = new URLSearchParams({ q: prezzSearch, top_k: '20', solo_opere: 'true' });
      if (selectedCatalogo) semParams.set('catalogo', selectedCatalogo);
      API.get(`/api/prezzario/semantic-search?${semParams.toString()}`)
        .then(r => setPrezzario(r.data?.results || []))
        .catch(() => {
          // Fallback su keyword search
          API.get('/api/prezzario', { params }).then(r => setPrezzario(r.data?.items || []));
        });
    } else {
      API.get('/api/prezzario', { params }).then(r => setPrezzario(r.data?.items || []));
    }
  }, [prezzSearch, selectedCatalogo]);

  const addRigaFromPrezzario = (voce) => {
    const riga = {
      codice_prezziario: voce.codice, descrizione: voce.descrizione,
      quantita: 1, unita_misura: voce.unita_misura, prezzo_unitario: voce.prezzo,
      tipo_riga: 'prezziario', totale_riga: voce.prezzo,
      // Multi-pagatore: di default attribuita al cliente principale
      pagatore_id: clientiPagatori[0]?.id || '',
      fuori_contabilita: false,
    };
    if (selectedSection !== null && form.sezioni[selectedSection]) {
      setForm(f => {
        const sez = [...f.sezioni];
        sez[selectedSection] = { ...sez[selectedSection], righe: [...(sez[selectedSection].righe || []), riga] };
        return { ...f, sezioni: sez };
      });
    } else {
      setForm(f => ({...f, righe: [...f.righe, riga]}));
    }
  };

  const addRigaManuale = () => {
    const riga = {
      codice_prezziario: '', descrizione: '', quantita: 1,
      unita_misura: 'cad', prezzo_unitario: 0, tipo_riga: 'personalizzata', totale_riga: 0,
      // Multi-pagatore: di default attribuita al cliente principale
      pagatore_id: clientiPagatori[0]?.id || '',
      fuori_contabilita: false,
    };
    if (selectedSection !== null && form.sezioni[selectedSection]) {
      setForm(f => {
        const sez = [...f.sezioni];
        sez[selectedSection] = { ...sez[selectedSection], righe: [...(sez[selectedSection].righe || []), riga] };
        return { ...f, sezioni: sez };
      });
    } else {
      setForm(f => ({...f, righe: [...f.righe, riga]}));
    }
  };

  const updateRiga = (idx, field, value) => {
    setForm(f => {
      const righe = [...f.righe];
      righe[idx] = {...righe[idx], [field]: value};
      righe[idx].totale_riga = parseFloat(righe[idx].quantita || 0) * parseFloat(righe[idx].prezzo_unitario || 0);
      return {...f, righe};
    });
  };

  const removeRiga = (idx) => setForm(f => ({...f, righe: f.righe.filter((_,i) => i !== idx)}));

  // ── Gestione SEZIONI ────────────────────────────────────────────
  const addSezione = () => {
    setForm(f => {
      const newSez = { nome: `Sezione ${(f.sezioni?.length || 0) + 1}`, oggetto: '', iva: f.iva_default || 22, note: '', righe: [] };
      const next = { ...f, sezioni: [...(f.sezioni || []), newSez] };
      // Se è la prima sezione e ci sono righe flat, sposta tutto dentro
      if ((f.sezioni?.length || 0) === 0 && (f.righe?.length || 0) > 0) {
        if (window.confirm('Hai già delle righe nel preventivo. Vuoi spostarle dentro questa nuova sezione?')) {
          next.sezioni[0].righe = [...f.righe];
          next.righe = [];
        }
      }
      return next;
    });
    setSelectedSection((form.sezioni?.length || 0));
  };

  // Importa una sezione clonata da un altro preventivo
  const handleImportSezione = (sezioneClonata) => {
    setForm(f => {
      const next = { ...f, sezioni: [...(f.sezioni || []), sezioneClonata] };
      return next;
    });
    setSelectedSection((form.sezioni?.length || 0));
  };

  const updateSezione = (idx, field, value) => {
    setForm(f => {
      const sez = [...f.sezioni];
      sez[idx] = { ...sez[idx], [field]: value };
      return { ...f, sezioni: sez };
    });
  };

  const removeSezione = (idx) => {
    if (!window.confirm('Eliminare la sezione e tutte le sue righe?')) return;
    setForm(f => ({ ...f, sezioni: f.sezioni.filter((_, i) => i !== idx) }));
    if (selectedSection === idx) setSelectedSection(null);
  };

  const moveSezione = (idx, delta) => {
    setForm(f => {
      const sez = [...f.sezioni];
      const newIdx = idx + delta;
      if (newIdx < 0 || newIdx >= sez.length) return f;
      [sez[idx], sez[newIdx]] = [sez[newIdx], sez[idx]];
      return { ...f, sezioni: sez };
    });
  };

  const updateRigaSezione = (sezIdx, rigaIdx, field, value) => {
    setForm(f => {
      const sez = [...f.sezioni];
      const righe = [...(sez[sezIdx].righe || [])];
      righe[rigaIdx] = { ...righe[rigaIdx], [field]: value };
      righe[rigaIdx].totale_riga = parseFloat(righe[rigaIdx].quantita || 0) * parseFloat(righe[rigaIdx].prezzo_unitario || 0);
      sez[sezIdx] = { ...sez[sezIdx], righe };
      return { ...f, sezioni: sez };
    });
  };

  const removeRigaSezione = (sezIdx, rigaIdx) => {
    setForm(f => {
      const sez = [...f.sezioni];
      sez[sezIdx] = { ...sez[sezIdx], righe: (sez[sezIdx].righe || []).filter((_, i) => i !== rigaIdx) };
      return { ...f, sezioni: sez };
    });
  };

  // Calcoli totali "live" UI
  const hasSezioni = (form.sezioni || []).length > 0;
  const ivaDef = parseFloat(form.iva_default || 22) || 22;
  const applicaIva = form.applica_iva !== false;
  const scontoPct = Math.max(0, Math.min(100, parseFloat(form.sconto_globale_pct || 0) || 0));

  const calcSubtotaleSezione = (sez) => {
    const ivaSez = parseFloat(sez.iva ?? ivaDef) || ivaDef;
    const imp = (sez.righe || []).reduce((s, r) => s + (parseFloat(r.quantita || 0) * parseFloat(r.prezzo_unitario || 0)), 0);
    const ivaEff = applicaIva ? ivaSez : 0;
    const impScontato = imp * (1 - scontoPct / 100);
    const iva = impScontato * ivaEff / 100;
    return { imp, iva, fin: impScontato + iva, ivaSez };
  };

  const totaleImponibileLordo = hasSezioni
    ? form.sezioni.reduce((s, sez) => s + calcSubtotaleSezione(sez).imp, 0)
    : form.righe.reduce((s, r) => s + (parseFloat(r.quantita || 0) * parseFloat(r.prezzo_unitario || 0)), 0);

  const totaleScontoImporto = totaleImponibileLordo * scontoPct / 100;
  const totaleImponibile = totaleImponibileLordo - totaleScontoImporto;

  const totaleIva = !applicaIva ? 0 : (hasSezioni
    ? form.sezioni.reduce((s, sez) => s + calcSubtotaleSezione(sez).iva, 0)
    : totaleImponibile * ivaDef / 100);

  const totale = totaleImponibile + totaleIva;

  const save = async () => {
    if (editing) {
      await API.put(`/api/preventivi/${editing}`, form);
      setEditing(null);
    } else {
      await API.post('/api/preventivi', form);
    }
    setShowNew(false);
    setEditing(null);
    setSelectedSection(null);
    setForm(resetForm());
    load();
  };

  const openEdit = (p) => {
    setForm({
      titolo: p.titolo || '', oggetto: p.oggetto || '', pratica_id: p.pratica_id || '',
      iva_default: p.iva_default ?? 22,
      applica_iva: p.applica_iva !== false,
      sconto_globale_pct: parseFloat(p.sconto_globale_pct || 0) || 0,
      note_finali: p.note_finali || '', esclusioni: p.esclusioni || '',
      righe: (p.righe || []).map(r => ({...r})),
      sezioni: (p.sezioni || []).map(s => ({...s, righe: (s.righe || []).map(r => ({...r}))})),
    });
    setEditing(p.id);
    setShowNew(true);
    setShowDetail(null);
  };

  const duplica = (p) => {
    setForm({
      titolo: `${p.titolo || p.numero_preventivo} (copia)`,
      oggetto: p.oggetto || '',
      iva_default: p.iva_default ?? 22,
      applica_iva: p.applica_iva !== false,
      sconto_globale_pct: parseFloat(p.sconto_globale_pct || 0) || 0,
      pratica_id: p.pratica_id || context?.pratica_id || '',
      note_finali: p.note_finali || '', esclusioni: p.esclusioni || '',
      righe: (p.righe || []).map(r => ({...r})),
      sezioni: (p.sezioni || []).map(s => ({...s, righe: (s.righe || []).map(r => ({...r}))})),
    });
    setEditing(null);
    setShowNew(true);
    setShowDetail(null);
  };

  const approva = async (id) => {
    if (!await confirm({
      title: 'Approva preventivo',
      message: 'Vuoi approvare questo preventivo?\nVerrà creato automaticamente un cantiere collegato.',
      confirmLabel: 'Approva e crea cantiere',
      variant: 'success',
    })) return;
    setActionLoading('approva-' + id);
    try {
      const { data } = await API.post(`/api/preventivi/${id}/approva`);
      alert(`Preventivo approvato! Cantiere ${data.cantiere?.codice_cantiere || ''} creato.`);
      load();
      setShowDetail(null);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setActionLoading('');
  };

  const valida = async (id) => {
    if (!await confirm({
      title: 'Valida prezzi',
      message: 'Vuoi validare i prezzi di questo preventivo?\nI prezzi aggiorneranno il listino aziendale.',
      confirmLabel: 'Valida prezzi',
      variant: 'info',
    })) return;
    setActionLoading('valida-' + id);
    try {
      const { data } = await API.post(`/api/preventivi/${id}/valida`);
      alert(`Prezzi validati! ${data.proposte_aggiornamento} aggiornamenti listino generati.`);
      load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setActionLoading('');
  };

  const downloadPdf = async (id, numero) => {
    setActionLoading('pdf-' + id);
    try {
      await downloadBlob(
        API.get(`/api/preventivi/${id}/pdf`, { responseType: 'blob' }),
        `${numero || 'Preventivo'}.pdf`,
        'application/pdf',
      );
    } catch (e) {
      alert('Errore download PDF: ' + await extractAxiosErrorMessage(e));
    }
    setActionLoading('');
  };

  const resetForm = () => ({ titolo: '', oggetto: '', iva_default: 22, applica_iva: true, sconto_globale_pct: 0, pratica_id: context?.pratica_id || '', note_finali: '', esclusioni: '', righe: [], sezioni: [] });

  // Chiusura sicura: se ci sono dati non salvati nel form chiede conferma
  const closeNewForm = () => {
    const hasData = form.titolo || form.oggetto || form.note_finali || form.esclusioni || (form.righe && form.righe.length > 0) || (form.sezioni && form.sezioni.length > 0);
    if (hasData && !editing) {
      if (!window.confirm('Hai dati non salvati nel preventivo. Vuoi davvero chiudere e perdere le modifiche?')) return;
    } else if (editing && hasData) {
      if (!window.confirm('Hai modifiche non salvate. Vuoi davvero chiudere?')) return;
    }
    setShowNew(false);
    setEditing(null);
    setForm(resetForm());
    setSelectedSection(null);
  };


  const downloadExcel = async (id, numero) => {
    setActionLoading('xlsx-' + id);
    try {
      await downloadBlob(
        API.get(`/api/preventivi/${id}/excel`, { responseType: 'blob' }),
        `${numero || 'Preventivo'}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (e) {
      alert('Errore download Excel: ' + await extractAxiosErrorMessage(e));
    }
    setActionLoading('');
  };

  const downloadElencoExcel = async () => {
    setActionLoading('elenco-xlsx');
    try {
      await downloadBlob(
        API.get('/api/preventivi/excel/elenco', { responseType: 'blob' }),
        'Preventivi_elenco.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (e) {
      alert('Errore export elenco: ' + await extractAxiosErrorMessage(e));
    }
    setActionLoading('');
  };

  const [aiProgress, setAiProgress] = useState('');

  const generateAI = async () => {
    setAiLoading(true);
    setAiProgress('Avvio generazione...');
    try {
      const { pollAiJob } = await import('../aiJobs');
      const { result, error } = await pollAiJob(
        '/api/ai/genera-preventivo',
        { descrizione: aiDesc, catalogo: selectedCatalogo || '' },
        { onProgress: setAiProgress }
      );
      if (error) {
        alert('Errore AI: ' + error);
      } else if (result?.righe) {
        setForm(f => ({ ...f, titolo: result.titolo || f.titolo, note_finali: result.note_finali || '', esclusioni: result.esclusioni || '', righe: result.righe || [] }));
        setShowAI(false);
        setShowNew(true);
      } else if (result?.errore) {
        alert('Errore parsing AI: ' + result.errore);
      }
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setAiLoading(false);
    setAiProgress('');
  };

  const statoLabels = { bozza: 'Bozza', pronto: 'Pronto', inviato: 'Inviato', approvato: 'Approvato', rifiutato: 'Rifiutato', validato: 'Validato' };
  const statoColors = { bozza: 'bg-slate-100 text-slate-700', pronto: 'bg-blue-100 text-blue-700', inviato: 'bg-purple-100 text-purple-700', approvato: 'bg-green-100 text-green-700', rifiutato: 'bg-red-100 text-red-700', validato: 'bg-emerald-100 text-emerald-700' };
  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  // ── Ricerca + filtri ────────────────────────────────────────────────
  const praticaById = useMemo(() => {
    const m = {};
    for (const p of pratiche) m[p.id] = p;
    return m;
  }, [pratiche]);
  const clienteById = useMemo(() => {
    const m = {};
    for (const c of clientiAll) {
      const nome = c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || c.email || '(senza nome)';
      m[c.id] = nome;
    }
    return m;
  }, [clientiAll]);
  const clienteForPreventivo = useCallback((p) => {
    const pr = praticaById[p.pratica_id];
    if (!pr) return '';
    const ids = (Array.isArray(pr.clienti_ids) && pr.clienti_ids.length) ? pr.clienti_ids : (pr.cliente_id ? [pr.cliente_id] : []);
    return ids.map(id => clienteById[id]).filter(Boolean).join(', ');
  }, [praticaById, clienteById]);
  const filteredPreventivi = useMemo(() => {
    const q = search.trim().toLowerCase();
    return preventivi.filter(p => {
      if (statoFilter && p.stato !== statoFilter) return false;
      if (clienteFilter) {
        const pr = praticaById[p.pratica_id];
        const ids = pr ? ((Array.isArray(pr.clienti_ids) && pr.clienti_ids.length) ? pr.clienti_ids : (pr.cliente_id ? [pr.cliente_id] : [])) : [];
        if (!ids.includes(clienteFilter)) return false;
      }
      if (q) {
        const cli = clienteForPreventivo(p).toLowerCase();
        const fields = [p.titolo, p.numero_preventivo, p.oggetto, p.note_finali, cli].filter(Boolean).map(s => String(s).toLowerCase());
        if (!fields.some(f => f.includes(q))) return false;
      }
      return true;
    });
  }, [preventivi, search, statoFilter, clienteFilter, praticaById, clienteForPreventivo]);

  return (
    <div className="animate-fade-in" data-testid="preventivi-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Preventivi</h1>
          {context?.pratica_titolo && <p className="text-slate-500 text-sm mt-1">Pratica: {context.pratica_titolo}</p>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button data-testid="export-elenco-excel" onClick={downloadElencoExcel} disabled={actionLoading === 'elenco-xlsx'}
            className="px-3 py-2 bg-emerald-600 text-white font-bold rounded-md hover:bg-emerald-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm disabled:opacity-50" title="Esporta tutti in Excel">
            <Download size={16} /> <span className="hidden sm:inline">{actionLoading === 'elenco-xlsx' ? 'Esporto...' : 'Excel'}</span>
          </button>
          <button data-testid="import-ai-preventivo-btn" onClick={() => setShowImportAI(true)}
            className="px-3 py-2 bg-purple-600 text-white font-bold rounded-md hover:bg-purple-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Wand2 size={16} /> <span className="hidden sm:inline">Importa con</span> AI
          </button>
          <button data-testid="ai-generate-btn" onClick={() => setShowAI(true)}
            className="px-3 py-2 bg-surface-dark text-white font-bold rounded-md hover:bg-slate-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Sparkles size={16} /> <span className="hidden sm:inline">Genera con</span> AI
          </button>
          <button data-testid="add-preventivo-btn" onClick={() => { setShowNew(true); setEditing(null); setForm({ titolo: '', pratica_id: context?.pratica_id || '', note_finali: '', esclusioni: '', righe: [] }); }}
            className="px-3 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Plus size={16} /> Nuovo
          </button>
        </div>
      </div>

      {/* Ricerca & Filtri */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-col sm:flex-row gap-2" data-testid="preventivi-search-bar">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="preventivi-search-input"
            placeholder="Cerca per numero, titolo, oggetto o cliente..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700" data-testid="preventivi-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <select
            data-testid="preventivi-stato-filter"
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
            value={statoFilter}
            onChange={e => setStatoFilter(e.target.value)}
          >
            <option value="">Tutti gli stati</option>
            {Object.entries(statoLabels).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <select
            data-testid="preventivi-cliente-filter"
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white max-w-[220px]"
            value={clienteFilter}
            onChange={e => setClienteFilter(e.target.value)}
          >
            <option value="">Tutti i clienti</option>
            {clientiAll.map(c => {
              const nome = c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || c.email || '(senza nome)';
              return <option key={c.id} value={c.id}>{nome}</option>;
            })}
          </select>
          {(search || statoFilter || clienteFilter) && (
            <button
              onClick={() => { setSearch(''); setStatoFilter(''); setClienteFilter(''); }}
              data-testid="preventivi-filters-clear"
              className="px-2 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-md flex items-center gap-1"
              title="Pulisci filtri"
            >
              <Filter size={14} /> Pulisci
            </button>
          )}
        </div>
      </div>

      {(search || statoFilter || clienteFilter) && (
        <p className="text-xs text-slate-500 mb-2" data-testid="preventivi-count">
          {filteredPreventivi.length} risultati su {preventivi.length}
        </p>
      )}

      {/* List */}
      <div className="space-y-3">
        {filteredPreventivi.length === 0 && (
          <div className="bg-white border border-dashed border-slate-200 rounded-md p-8 text-center text-sm text-slate-400" data-testid="preventivi-empty">
            {preventivi.length === 0 ? 'Nessun preventivo ancora creato.' : 'Nessun preventivo corrisponde ai filtri.'}
          </div>
        )}
        {filteredPreventivi.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-all">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-sm sm:text-base truncate">{p.titolo || p.numero_preventivo}</h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${statoColors[p.stato] || 'bg-slate-100'}`}>{statoLabels[p.stato] || p.stato}</span>
                </div>
                {p.oggetto && <p className="text-xs italic text-slate-500 truncate mt-0.5">{p.oggetto}</p>}
                <p className="text-xs text-slate-400 mt-0.5">
                  {p.numero_preventivo}
                  {clienteForPreventivo(p) && <> · <span className="text-slate-500">{clienteForPreventivo(p)}</span></>}
                  {' '}-
                  {(p.sezioni && p.sezioni.length > 0)
                    ? <> <Layers size={10} className="inline -mt-0.5" /> {p.sezioni.length} sezioni · {p.sezioni.reduce((s, sz) => s + (sz.righe?.length || 0), 0)} righe</>
                    : <> {p.righe?.length || 0} righe</>}
                </p>
              </div>
              <div className="text-left sm:text-right flex-shrink-0">
                <p className="text-lg sm:text-xl font-black font-mono">{fmt(p.totale_finale)}</p>
                <p className="text-xs text-slate-400">IVA inclusa</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <button onClick={() => setShowDetail(p)} className="text-xs text-primary font-semibold hover:underline" data-testid={`detail-${p.id}`}>Dettaglio</button>
              <button onClick={() => downloadPdf(p.id, p.numero_preventivo)} disabled={actionLoading === 'pdf-' + p.id}
                className="text-xs text-accent-info font-semibold hover:underline flex items-center gap-1 disabled:opacity-50" data-testid={`pdf-${p.id}`}>
                <Download size={12}/>{actionLoading === 'pdf-' + p.id ? 'Scaricamento...' : 'PDF'}
              </button>
              <button onClick={() => downloadExcel(p.id, p.numero_preventivo)} disabled={actionLoading === 'xlsx-' + p.id}
                className="text-xs text-emerald-600 font-semibold hover:underline flex items-center gap-1 disabled:opacity-50" data-testid={`xlsx-${p.id}`}>
                <Download size={12}/>{actionLoading === 'xlsx-' + p.id ? '...' : 'Excel'}
              </button>
              {p.stato === 'bozza' && (
                <>
                  <button onClick={() => openEdit(p)} className="text-xs text-slate-600 font-semibold hover:underline flex items-center gap-1" data-testid={`edit-${p.id}`}><Edit2 size={12}/>Modifica</button>
                  <button onClick={() => approva(p.id)} disabled={actionLoading === 'approva-' + p.id}
                    className="text-xs text-accent-success font-semibold hover:underline flex items-center gap-1 disabled:opacity-50" data-testid={`approva-${p.id}`}>
                    <Check size={12}/>{actionLoading === 'approva-' + p.id ? 'Approvazione...' : 'Approva e Apri Cantiere'}
                  </button>
                </>
              )}
              {(p.stato === 'bozza' || p.stato === 'approvato') && (
                <button onClick={() => valida(p.id)} disabled={actionLoading === 'valida-' + p.id}
                  className="text-xs text-purple-600 font-semibold hover:underline flex items-center gap-1 disabled:opacity-50" data-testid={`valida-${p.id}`}>
                  <Check size={12}/>{actionLoading === 'valida-' + p.id ? 'Validazione...' : 'Valida Prezzi'}
                </button>
              )}
              <button onClick={() => duplica(p)} className="text-xs text-slate-400 font-semibold hover:underline flex items-center gap-1"><Copy size={12}/>Duplica</button>
              <button onClick={() => openLink('pratica', p)} className={`text-xs font-semibold hover:underline flex items-center gap-1 ${p.pratica_id ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`link-pratica-${p.id}`} title={p.pratica_id ? `Pratica: ${getName(getInfo('pratica', p.pratica_id), 'pratica') || ''}` : 'Collega pratica'}>
                <Link2 size={12}/>{p.pratica_id ? `Pratica: ${(getName(getInfo('pratica', p.pratica_id), 'pratica') || '').slice(0, 16) || '✓'}` : 'Pratica'}
              </button>
              <button onClick={() => openLink('cantiere', p)} className={`text-xs font-semibold hover:underline flex items-center gap-1 ${p.cantiere_id ? 'text-amber-600' : 'text-slate-400'}`} data-testid={`link-cantiere-${p.id}`} title={p.cantiere_id ? `Cantiere: ${getName(getInfo('cantiere', p.cantiere_id), 'cantiere') || ''}` : 'Collega cantiere'}>
                <Link2 size={12}/>{p.cantiere_id ? `Cantiere: ${(getName(getInfo('cantiere', p.cantiere_id), 'cantiere') || '').slice(0, 14) || '✓'}` : 'Cantiere'}
              </button>
              <button onClick={() => openLink('computo', p)} className={`text-xs font-semibold hover:underline flex items-center gap-1 ${p.computo_id ? 'text-sky-600' : 'text-slate-400'}`} data-testid={`link-computo-${p.id}`} title={p.computo_id ? `Computo: ${getName(getInfo('computo', p.computo_id), 'computo') || ''}` : 'Collega computo'}>
                <Link2 size={12}/>{p.computo_id ? `Computo: ${(getName(getInfo('computo', p.computo_id), 'computo') || '').slice(0, 14) || '✓'}` : 'Computo'}
              </button>
              <span className="ml-auto">
                <RowActions entity="preventivi" id={p.id} label="il preventivo" onChange={load} compact />
              </span>
            </div>
          </div>
        ))}
        {preventivi.length === 0 && <p className="text-center text-slate-400 py-8">Nessun preventivo. Creane uno o usa l'AI!</p>}
      </div>

      {/* Detail Modal */}
      <PreventivoDetailModal
        preventivo={showDetail}
        actionLoading={actionLoading}
        onClose={() => setShowDetail(null)}
        onEdit={openEdit}
        onDownloadPdf={downloadPdf}
        onApprova={approva}
        onValida={valida}
        onDuplica={duplica}
      />

      {/* New/Edit Preventivo Form */}
      {showNew && (
        <PreventivoFormModal
          form={form}
          setForm={setForm}
          editing={editing}
          onClose={closeNewForm}
          onSave={save}
          hasSezioni={hasSezioni}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          ivaDef={ivaDef}
          addSezione={addSezione}
          updateSezione={updateSezione}
          removeSezione={removeSezione}
          moveSezione={moveSezione}
          calcSubtotaleSezione={calcSubtotaleSezione}
          updateRigaSezione={updateRigaSezione}
          removeRigaSezione={removeRigaSezione}
          onImportSezione={() => setShowImportSezione(true)}
          addRigaManuale={addRigaManuale}
          updateRiga={updateRiga}
          removeRiga={removeRiga}
          cataloghi={cataloghi}
          selectedCatalogo={selectedCatalogo}
          setSelectedCatalogo={setSelectedCatalogo}
          prezzSearch={prezzSearch}
          setPrezzSearch={setPrezzSearch}
          prezzario={prezzario}
          addRigaFromPrezzario={addRigaFromPrezzario}
          totaleImponibile={totaleImponibile}
          totaleIva={totaleIva}
          totale={totale}
          totaleScontoImporto={totaleScontoImporto}
          clientiPagatori={clientiPagatori}
        />
      )}

      {/* AI Generation Modal */}
      {showAI && (
        <PreventivoAIGenModal
          cataloghi={cataloghi}
          selectedCatalogo={selectedCatalogo}
          onSelectCatalogo={setSelectedCatalogo}
          aiDesc={aiDesc}
          onChangeDesc={setAiDesc}
          aiLoading={aiLoading}
          aiProgress={aiProgress}
          onSubmit={generateAI}
          onClose={() => setShowAI(false)}
        />
      )}

      {/* Link Modal (pratica / cantiere / computo) */}
      {linkModal && (
        <PreventivoLinkModal
          linkModal={linkModal}
          cfg={LINK_CONFIG[linkModal.tipo]}
          onClose={() => setLinkModal(null)}
          onSearchChange={(s) => setLinkModal({ ...linkModal, search: s })}
          onLink={doLink}
          onUnlink={doUnlink}
          getInfo={getInfo}
          getName={getName}
        />
      )}
      {showImportAI && (
        <ImportAIModal tipo="preventivo" onClose={() => setShowImportAI(false)} onImport={handleImportAI} />
      )}
      {showImportSezione && (
        <ImportSezioneModal onClose={() => setShowImportSezione(false)} onPick={handleImportSezione} />
      )}
    </div>
  );
}
